/* ==========================================================================
   OliveDrab — header behaviour
   Sticky and auto hide, mega menu with hover intent, mobile drawer with a
   focus trap, search overlay, and live cart / wishlist counts.

   No dependencies. Loaded with defer.
   Designed by Thriftizer.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.ODConfig || {};

  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function reduced() {
    return window.OD && window.OD.prefersReduced ? window.OD.prefersReduced() : false;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), ' +
                  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------- scroll lock */

  var lockCount = 0;
  var savedScroll = 0;

  function lock() {
    if (lockCount === 0) {
      savedScroll = window.pageYOffset;
      document.documentElement.classList.add('od-locked');
    }
    lockCount++;
  }

  function unlock() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.documentElement.classList.remove('od-locked');
      window.scrollTo(0, savedScroll);
    }
  }

  /* ------------------------------------------------------- focus trap */

  function trap(container, onEscape) {
    function onKey(e) {
      if (e.key === 'Escape') { onEscape(); return; }
      if (e.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    container.addEventListener('keydown', onKey);
    return function () { container.removeEventListener('keydown', onKey); };
  }

  /* ==================================================== header element */

  var ODHeader = function () {};

  if (typeof HTMLElement === 'function') {
    ODHeader = function () {
      return Reflect.construct(HTMLElement, [], ODHeader);
    };
    ODHeader.prototype = Object.create(HTMLElement.prototype);
    ODHeader.prototype.constructor = ODHeader;
    Object.setPrototypeOf(ODHeader, HTMLElement);

    ODHeader.prototype.connectedCallback = function () {
      if (this._init) return;
      this._init = true;
      try { this.setup(); } catch (e) {
        if (window.console && console.warn) console.warn('[OD header]', e);
      }
    };

    ODHeader.prototype.setup = function () {
      this.sticky = this.dataset.sticky === 'true';
      this.autohide = this.dataset.autohide === 'true';
      this.transparent = this.dataset.transparent === 'true';
      this.megaOn = this.dataset.mega === 'true';
      this.megaMode = this.dataset.megaTrigger || 'hover';
      this.megaDelay = parseInt(this.dataset.megaDelay, 10) || 0;

      this.measure();
      this.initSticky();
      if (this.megaOn) this.initMega();
      this.initOverlay();

      var self = this;
      window.addEventListener('resize', function () { self.measure(); }, { passive: true });
    };

    /* ------------------------------------------------------- measure */

    ODHeader.prototype.measure = function () {
      var rect = this.getBoundingClientRect();
      var bottom = Math.max(0, rect.bottom);
      this.style.setProperty('--od-mega-top', bottom + 'px');
      var scrim = $('.od-mega-scrim');
      if (scrim) scrim.style.setProperty('--od-mega-top', bottom + 'px');
      this.headerH = rect.height;
    };

    /* -------------------------------------------------------- sticky */

    ODHeader.prototype.initSticky = function () {
      var self = this;
      var ticker = document.querySelector('.od-ticker');
      var fadeOver = parseInt(this.dataset.tickerFade, 10) || 0;
      var fadeOff = !ticker || !fadeOver;

      if (!this.sticky && !this.transparent && fadeOff) return;

      var spacer = document.querySelector('.od-header__spacer');
      var lastY = window.pageYOffset;
      var threshold = this.transparent ? Math.max(80, this.headerH) : this.headerH;

      /*
        Where the header sits in the page before it docks: below the
        announcement bar. Docking only once the page has scrolled that far,
        and holding its place with the spacer while docked, means nothing on
        the page moves at the moment it happens.
      */
      var holder = this.parentElement || this;
      var dockAt = 0;
      function measureDock() {
        if (self.classList.contains('is-stuck')) return;
        dockAt = Math.max(0, holder.getBoundingClientRect().top + window.pageYOffset);
      }
      measureDock();
      window.addEventListener('resize', measureDock, { passive: true });
      if (spacer) spacer.style.height = '0px';

      var onScroll = (window.OD && window.OD.rafThrottle)
        ? window.OD.rafThrottle(update)
        : update;

      /* where the last change of mind happened: the header only hides or
         shows again once the page has moved a clear distance from it */
      var anchorY = lastY;
      var TURN = 24;

      function update() {
        /* iOS bounces past both ends of the page; those positions are not
           real scrolling and must not dock, undock, hide or show anything */
        var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        var y = Math.max(0, Math.min(window.pageYOffset, maxY));

        // the announcement bar fades out over the first stretch of scrolling
        if (!fadeOff) {
          var o = Math.max(0, Math.min(1, 1 - y / fadeOver));
          ticker.style.opacity = o.toFixed(3);
          ticker.style.visibility = o === 0 ? 'hidden' : '';
        }

        if (self.transparent) {
          if (y > threshold) self.classList.add('is-solid');
          else self.classList.remove('is-solid');
        } else if (self.sticky) {
          // re-measured while undocked: fonts and stylesheets arriving late
          // change how far down the header starts
          measureDock();
          if (y > dockAt) {
            if (!self.classList.contains('is-stuck')) {
              if (spacer) spacer.style.height = self.offsetHeight + 'px';
              self.classList.add('is-stuck');
            }
          } else if (self.classList.contains('is-stuck')) {
            self.classList.remove('is-stuck');
            if (spacer) spacer.style.height = '0px';
          }
        }

        /* Auto hide, never while a panel, drawer or overlay is open.
           Deciding afresh on every frame made the header twitch on a phone:
           momentum scrolling flips direction a pixel at a time, so it kept
           starting to slide away and coming back. It now waits until the
           page has travelled TURN pixels in one direction, and only ever
           hides once it is docked. */
        if (self.autohide && !self.dataset.busy) {
          var hidden = self.classList.contains('is-hidden');
          var nearTop = y <= Math.max(threshold * 2, dockAt + self.offsetHeight);
          if (nearTop || !self.classList.contains('is-stuck')) {
            if (hidden) self.classList.remove('is-hidden');
            anchorY = y;
          } else if (hidden) {
            if (y > anchorY) anchorY = y;
            else if (anchorY - y > TURN) { self.classList.remove('is-hidden'); anchorY = y; }
          } else {
            if (y < anchorY) anchorY = y;
            else if (y - anchorY > TURN) { self.classList.add('is-hidden'); anchorY = y; }
          }
        }

        lastY = y;
        self.measure();
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      update();
    };

    /* ---------------------------------------------------------- mega */

    ODHeader.prototype.initMega = function () {
      var self = this;
      var items = $$('[data-od-mega-item]', this);
      if (!items.length) return;

      var scrim = document.createElement('div');
      scrim.className = 'od-mega-scrim';
      document.body.appendChild(scrim);
      this.scrim = scrim;

      this.openItem = null;

      items.forEach(function (item) {
        var btn = $('[data-od-mega-btn]', item);
        var panel = $('[data-od-mega-panel]', item);
        if (!btn || !panel) return;

        $$('.od-mega__col', panel).forEach(function (col, i) {
          col.style.setProperty('--od-i', i);
        });

        if (self.megaMode === 'hover') {
          var openTimer, closeTimer;

          function scheduleOpen() {
            clearTimeout(closeTimer);
            clearTimeout(openTimer);
            openTimer = setTimeout(function () { self.openMega(item, btn, panel); }, self.megaDelay);
          }
          function scheduleClose() {
            clearTimeout(openTimer);
            clearTimeout(closeTimer);
            closeTimer = setTimeout(function () { self.closeMega(item, btn, panel); }, 140);
          }

          item.addEventListener('mouseenter', scheduleOpen);
          item.addEventListener('mouseleave', scheduleClose);
          panel.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
          panel.addEventListener('mouseleave', scheduleClose);

          // keyboard users still need it to open
          btn.addEventListener('focus', function () { self.openMega(item, btn, panel); });
          item.addEventListener('focusout', function (e) {
            if (!item.contains(e.relatedTarget)) self.closeMega(item, btn, panel);
          });
        } else {
          btn.addEventListener('click', function (e) {
            var isOpen = panel.classList.contains('is-open');
            // in click mode a top level link should open the panel, not navigate
            if (btn.tagName === 'A' && !isOpen) e.preventDefault();
            if (isOpen) self.closeMega(item, btn, panel);
            else self.openMega(item, btn, panel);
          });
        }
      });

      scrim.addEventListener('mouseenter', function () { self.closeAllMega(); });
      scrim.addEventListener('click', function () { self.closeAllMega(); });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.openItem) {
          var btn = $('[data-od-mega-btn]', self.openItem);
          self.closeAllMega();
          if (btn) btn.focus();
        }
      });
    };

    ODHeader.prototype.openMega = function (item, btn, panel) {
      if (this.openItem && this.openItem !== item) this.closeAllMega();
      this.measure();
      panel.hidden = false;
      // force a frame so the clip transition runs
      var raf = window.requestAnimationFrame || function (f) { f(); };
      raf(function () { panel.classList.add('is-open'); });
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      if (this.scrim) this.scrim.classList.add('is-open');
      this.openItem = item;
      this.dataset.busy = 'mega';
      this.classList.remove('is-hidden');
    };

    ODHeader.prototype.closeMega = function (item, btn, panel) {
      panel.classList.remove('is-open');
      item.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
      if (!this.openItem) {
        if (this.scrim) this.scrim.classList.remove('is-open');
        delete this.dataset.busy;
      }
      var delay = reduced() ? 0 : 420;
      setTimeout(function () {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, delay);
    };

    ODHeader.prototype.closeAllMega = function () {
      var self = this;
      $$('[data-od-mega-item]', this).forEach(function (item) {
        var btn = $('[data-od-mega-btn]', item);
        var panel = $('[data-od-mega-panel]', item);
        if (btn && panel) self.closeMega(item, btn, panel);
      });
    };

    /* ------------------------------------------------- search overlay */

    ODHeader.prototype.initOverlay = function () {
      var self = this;
      var overlay = $('[data-od-search-overlay]', this);
      var openers = $$('[data-od-search-open]');
      if (!openers.length) return;

      var inlineField = $('.od-header__search .od-search__input', this);
      var release = null;

      function openOverlay() {
        if (!overlay) return;
        overlay.hidden = false;
        var raf = window.requestAnimationFrame || function (f) { f(); };
        raf(function () { overlay.classList.add('is-open'); });
        lock();
        self.dataset.busy = 'search';
        self.classList.remove('is-hidden');
        release = trap(overlay, closeOverlay);
        var field = $('.od-search__input', overlay);
        if (field) setTimeout(function () { field.focus(); }, 60);
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      }

      function closeOverlay() {
        if (!overlay) return;
        overlay.classList.remove('is-open');
        unlock();
        delete self.dataset.busy;
        if (release) { release(); release = null; }
        openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        setTimeout(function () {
          if (!overlay.classList.contains('is-open')) overlay.hidden = true;
        }, reduced() ? 0 : 300);
      }

      openers.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          // on desktop with an inline field, just focus it
          if (inlineField && inlineField.offsetParent !== null) {
            inlineField.focus();
            return;
          }
          openOverlay();
        });
      });

      $$('[data-od-search-close]', overlay || document).forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); closeOverlay(); });
      });
    };

    try { customElements.define('od-header', ODHeader); } catch (e) { /* already defined */ }
  }

  /* ========================================================== drawer */

  function initDrawer() {
    var drawer = $('[data-od-drawer]');
    if (!drawer) return;

    var openers = $$('[data-od-drawer-open]');
    var closers = $$('[data-od-drawer-close]', drawer);
    var panel = $('.od-drawer__panel', drawer);
    var release = null;
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      var raf = window.requestAnimationFrame || function (f) { f(); };
      raf(function () { drawer.classList.add('is-open'); });
      lock();
      release = trap(panel, close);
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var first = $(FOCUSABLE, panel);
      if (first) setTimeout(function () { first.focus(); }, 80);
    }

    function close() {
      drawer.classList.remove('is-open');
      unlock();
      if (release) { release(); release = null; }
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, reduced() ? 0 : 420);
    }

    openers.forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (drawer.classList.contains('is-open')) close();
        else open();
      });
    });

    closers.forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });

    // accordions
    $$('[data-od-drawer-toggle]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sub = btn.nextElementSibling;
        if (!sub) return;
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!isOpen));
        sub.hidden = isOpen;
      });
    });

    // close when the viewport grows into the desktop nav
    var mq = window.matchMedia('(min-width: 990px)');
    var onChange = function (e) { if (e.matches && drawer.classList.contains('is-open')) close(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  /* ==================================================== live counters */

  function setCount(selector, value) {
    $$(selector).forEach(function (el) {
      el.textContent = value;
      el.hidden = !value || value === 0 || value === '0';
    });
  }

  function refreshCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) setCount('[data-od-cart-count]', cart.item_count);
      })
      .catch(function () { /* leave the server rendered count in place */ });
  }

  document.addEventListener('od:cart:changed', refreshCart);

  // Dawn publishes cart updates through its own pubsub; listen for both
  document.addEventListener('cart:refresh', refreshCart);

  /* ============================================================= init */

  function boot() {
    try { initDrawer(); } catch (e) {
      if (window.console && console.warn) console.warn('[OD drawer]', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-od-drawer]')) boot();
  });

  window.OD = window.OD || {};
  window.OD.lockScroll = lock;
  window.OD.unlockScroll = unlock;
  window.OD.focusTrap = trap;
  window.OD.setCount = setCount;
  window.OD.refreshCart = refreshCart;
})();
